import React, { useState } from 'react';
import HotelTitle from './HotelTitle';
import { CompetitorAnalysisComparison } from '../../../../types/competitors';
import useTranslate from '../../../../../../customHooks/useTranslate';
import '../../../../stylesheets/competitor-viewer.scss';
import { Tabs, Tab } from '@mui/material';
import CompetitorCategorySection from './CompetitorCategorySection';
import NoServicesInSection from './NoServicesInSection';
import CompetitorViewerStats from './stats/CompetitorViewerStats';

interface CompetitorViewerProps {
    competitorName: string;
    data?: CompetitorAnalysisComparison;
    ownName: string;
}

enum CompetitorServicesFilter {
    ALL_CONCEPTS = 'ALL_CONCEPTS',
    GAPS = 'GAPS',
    ADVANTAGES = 'ADVANTAGES',
    PARITY = 'PARITY',
}

const CompetitorViewer = ({
    data,
    ownName,
    competitorName,
}: CompetitorViewerProps) => {
    const translate = useTranslate();

    const [selectedFilter, setSelectedFilter] = useState(
        CompetitorServicesFilter.ALL_CONCEPTS
    );
    const allServices = [
        ...(data?.advantagesOverCompetitor || []),
        ...(data?.gapsVsCompetitor || []),
        ...(data?.matchedServices || []),
    ].filter(
        (service, index, services) =>
            services.findIndex(item => item.code === service.code) === index
    );
    const servicesByFilter = {
        [CompetitorServicesFilter.ALL_CONCEPTS]: allServices,
        [CompetitorServicesFilter.GAPS]: data?.gapsVsCompetitor || [],
        [CompetitorServicesFilter.ADVANTAGES]:
            data?.advantagesOverCompetitor || [],
        [CompetitorServicesFilter.PARITY]: data?.matchedServices || [],
    };
    const filteredServices = [...servicesByFilter[selectedFilter]].sort(
        (a, b) => a.name.localeCompare(b.name)
    );

    const groupedServices = filteredServices.reduce((acc, service) => {
        if (!acc[service.category]) {
            acc[service.category] = [];
        }
        acc[service.category].push(service);
        return acc;
    }, {} as Record<string, typeof filteredServices>);

    const groupedServicesArray = Object.entries(groupedServices).sort(
        ([firstCategory], [secondCategory]) =>
            firstCategory.localeCompare(secondCategory)
    );

    return (
        <div className='competitor-viewer'>
            <header className='competitor-viewer__header'>
                <HotelTitle
                    name={ownName}
                    title={translate('our_property')}
                    subtitle={`${data?.ownServiceCount} ${translate(
                        'services_configured'
                    )}`}
                    type='main'
                />
                <p className='competitor-viewer__vs'>VS</p>
                <HotelTitle
                    name={competitorName}
                    title={translate('competitor')}
                    subtitle={`${data?.competitorServiceCount} ${translate(
                        'services_found'
                    )}`}
                    type='secondary'
                />
            </header>
            <CompetitorViewerStats data={data} />
            <main className='competitor-viewer__main'>
                <Tabs
                    value={selectedFilter}
                    onChange={(e, newValue) => setSelectedFilter(newValue)}
                    className='competitor-viewer__tabs'
                >
                    <Tab
                        label={`${translate('all')} (${
                            servicesByFilter[
                                CompetitorServicesFilter.ALL_CONCEPTS
                            ].length
                        })`}
                        value={CompetitorServicesFilter.ALL_CONCEPTS}
                    />
                    <Tab
                        label={`${translate('gaps_competitor_only')} (${
                            servicesByFilter[CompetitorServicesFilter.GAPS]
                                .length
                        })`}
                        value={CompetitorServicesFilter.GAPS}
                    />
                    <Tab
                        label={`${translate('our_advantages')} (${
                            servicesByFilter[
                                CompetitorServicesFilter.ADVANTAGES
                            ].length
                        })`}
                        value={CompetitorServicesFilter.ADVANTAGES}
                    />
                    <Tab
                        label={`${translate('parity')} (${
                            servicesByFilter[CompetitorServicesFilter.PARITY]
                                .length
                        })`}
                        value={CompetitorServicesFilter.PARITY}
                    />
                </Tabs>
                {groupedServicesArray.length ? (
                    groupedServicesArray.map(([categoryName, services]) => (
                        <CompetitorCategorySection
                            key={categoryName}
                            categoryName={categoryName}
                            services={services}
                            hotelName={ownName}
                            competitorName={competitorName}
                        />
                    ))
                ) : (
                    <NoServicesInSection />
                )}
            </main>
        </div>
    );
};

export default CompetitorViewer;
